import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { blogPosts } from "../pages/Blog/data/blogData";
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  PAGE_SEO,
  CANONICAL_ALIASES,
  type PageSeo,
} from "../seo/seoConfig";

/* ---------- helpers: update the tag if it exists, create it if it doesn't ---------- */

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/* ---------- resolve the current URL to its metadata ---------- */

function resolve(pathname: string): { seo: PageSeo | null; canonicalPath: string } {
  // strip a trailing slash so "/projects/" and "/projects" behave identically
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";

  const aliasOf = CANONICAL_ALIASES[path];
  if (aliasOf) return { seo: PAGE_SEO[aliasOf] ?? null, canonicalPath: aliasOf };

  const staticSeo = PAGE_SEO[path];
  if (staticSeo) return { seo: staticSeo, canonicalPath: path };

  // blog posts are dynamic: /blogs/:slug
  const slug = path.startsWith("/blogs/") ? path.slice("/blogs/".length) : null;
  if (slug) {
    const post = blogPosts.find((p) => p.slug === slug);
    if (post) {
      return {
        seo: {
          title: post.seoTitle ?? `${post.title} | Saleem Enterprises`,
          description: post.seoDescription ?? post.excerpt,
        },
        canonicalPath: path,
      };
    }
  }

  return { seo: null, canonicalPath: path };
}

/**
 * Applies per-page title, description, canonical and Open Graph tags.
 * Unknown URLs (the thousands of dead links left by the previous site) are
 * marked noindex so search engines stop trying to index them.
 */
export default function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const { seo, canonicalPath } = resolve(pathname);
    const url = `${SITE_URL}${canonicalPath === "/" ? "/" : canonicalPath}`;

    if (!seo) {
      document.title = "Page Not Found | Saleem Enterprises";
      upsertMeta("name", "robots", "noindex, follow");
      // The SPA fallback serves the prerendered homepage, so a stale canonical
      // can be sitting in the head. Remove it — noindex plus "I am the homepage"
      // is a mixed signal.
      document.head.querySelector('link[rel="canonical"]')?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
      return;
    }

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "robots", "index, follow");
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "Saleem Enterprises");
    upsertMeta("property", "og:image", `${SITE_URL}${DEFAULT_OG_IMAGE}`);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", `${SITE_URL}${DEFAULT_OG_IMAGE}`);
  }, [pathname]);

  return null;
}
